import { useEffect, useState, useCallback } from "react";
import {
  IcPageHeader,
  IcButton,
  IcTextField,
  IcSelect,
  IcAlert,
} from "@ukic/react";

import { api, type User, type UserRole } from "../api";
import { DataTable, type Column } from "../components/DataTable";
import { Loading } from "../components/Loading";
import { ErrorState } from "../components/ErrorState";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useRole } from "../useRole";
import { useToast } from "../toast";

const ROLE_OPTIONS = [
  { label: "User", value: "user" },
  { label: "Admin", value: "admin" },
];

export default function Users() {
  const { isAdmin } = useRole();
  const toast = useToast();

  const [users, setUsers] = useState<User[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<UserRole>("user");
  const [adding, setAdding] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<User | null>(null);

  const load = useCallback(() => {
    setError(null);
    api.listUsers().then(setUsers).catch(setError);
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const add = useCallback(async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true);
    try {
      await api.addUser(email, newRole);
      toast.success("User added", `${email} can now sign in.`);
      setNewEmail("");
      setNewRole("user");
      load();
    } catch (e) {
      toast.error("Could not add user", e instanceof Error ? e.message : String(e));
    } finally {
      setAdding(false);
    }
  }, [newEmail, newRole, toast, load]);

  const changeRole = useCallback(
    async (u: User, role: UserRole) => {
      if (role === u.role) return;
      try {
        await api.setUserRole(u.email, role);
        toast.success("Role updated", `${u.email} is now ${role}.`);
        load();
      } catch (e) {
        toast.error("Could not update role", e instanceof Error ? e.message : String(e));
      }
    },
    [toast, load],
  );

  const remove = useCallback(
    async (u: User) => {
      try {
        await api.removeUser(u.email);
        toast.success("User removed", `${u.email} can no longer sign in.`);
        load();
      } catch (e) {
        toast.error("Could not remove user", e instanceof Error ? e.message : String(e));
      } finally {
        setPendingRemove(null);
      }
    },
    [toast, load],
  );

  if (!isAdmin) {
    return (
      <div className="hl-page">
        <IcAlert
          variant="info"
          heading="Admins only"
          message="You need the admin role to manage users."
        />
      </div>
    );
  }

  const columns: Column<User>[] = [
    { key: "email", header: "Email", cell: (u) => <strong>{u.email}</strong> },
    {
      key: "role",
      header: "Role",
      cell: (u) => (
        <IcSelect
          label="Role"
          hideLabel
          value={u.role}
          options={ROLE_OPTIONS}
          onIcChange={(e) =>
            changeRole(u, (e as CustomEvent<{ value: string }>).detail.value as UserRole)
          }
        />
      ),
    },
    {
      key: "action",
      header: "",
      align: "right",
      cell: (u) => (
        <IcButton
          variant="destructive"
          size="small"
          onClick={() => setPendingRemove(u)}
        >
          Remove
        </IcButton>
      ),
    },
  ];

  return (
    <div className="hl-page hl-stack">
      <IcPageHeader heading="Users" subheading="Who can access HORIZONLOOK">
        <IcButton slot="actions" variant="secondary" onClick={load}>
          Refresh
        </IcButton>
      </IcPageHeader>

      <div className="hl-panel hl-stack">
        <h2>Add a user</h2>
        <p className="hl-muted">
          Adding a user also grants them access to the site via the Cloudflare
          Access group (SPEC §3.6). Removing reverses it.
        </p>
        <div className="hl-row">
          <IcTextField
            label="Email"
            type="email"
            placeholder="name@example.com"
            value={newEmail}
            onIcInput={(e) =>
              setNewEmail((e as CustomEvent<{ value: string }>).detail.value)
            }
          />
          <IcSelect
            label="Role"
            value={newRole}
            options={ROLE_OPTIONS}
            onIcChange={(e) =>
              setNewRole((e as CustomEvent<{ value: string }>).detail.value as UserRole)
            }
          />
          <IcButton onClick={add} loading={adding} disabled={!newEmail.trim()}>
            Add user
          </IcButton>
        </div>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : users === null ? (
        <Loading label="Loading users…" />
      ) : (
        <DataTable
          caption="Users"
          columns={columns}
          rows={users}
          rowKey={(u) => u.email}
          emptyMessage="No users."
        />
      )}

      <ConfirmDialog
        open={pendingRemove !== null}
        heading="Remove user?"
        message={
          pendingRemove
            ? `Remove ${pendingRemove.email}? They will lose access to HORIZONLOOK.`
            : ""
        }
        destructive
        onConfirm={() => pendingRemove && remove(pendingRemove)}
        onCancel={() => setPendingRemove(null)}
      />
    </div>
  );
}
